import { useMutation, useQuery } from '@apollo/client';
import { Actions, Button, Divider, Header, Loader, Modal, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useMemberNameModalAvailability from '../../../lib/hooks/useMemberNameModalAvailability';
import useProfile from '../../../lib/hooks/useProfile';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import { RATING_EXPERIENCE } from '../../constants/reviewRatings';
import StationPropType from '../../prop-types/station';
import EditMemberNameModal from '../EditMemberNameModal';
import ReviewQuestionItem from '../ReviewQuestionItem';
import StationInfoBox from '../StationInfoBox';
import ThankYou from '../ThankYou';
import styles from './SubmitReview.module.css';
import WriteReview from './WriteReview';

const cx = classnames.bind(styles);

export const GET_REVIEW_SETTINGS = gql`
  query GetReviewSettings($stationId: ID!) {
    reviewSettings(stationId: $stationId) {
      disabled
      disabledMessage
      ratingCategories {
        id
        name
        question
        imageUrl
      }
    }
  }
`;

export const SUBMIT_REVIEW = gql`
  mutation SubmitReview($stationId: ID!, $body: String!, $replyRequested: Boolean!, $ratings: [RatingInput!]!, $recaptchaResponse: String!) {
    submitReview(stationId: $stationId, body: $body, replyRequested: $replyRequested, ratings: $ratings, recaptchaResponse: $recaptchaResponse)
  }
`;

export default function SubmitReview({
  notLoggedInLinkText,
  openModalLinkText,
  station,
}) {
  const { isLoggedIn, memberName = '', canSetMemberName } = useProfile();
  const [showMemberNameCustomization, setShowMemberNameCustomization] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [recordedRatings, setRecordedRatings] = useState([]);
  const [reviewText, setReviewText] = useState('');
  const [recaptchaAttempted, setRecaptchaAttempted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState();
  const { t } = useTranslation(['review', 'ratingCategories', 'miscellaneous']);
  const analytics = useAnalytics();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [shouldShowMemberNameModal, setLastShownMemberNameModal] = useMemberNameModalAvailability();

  const { id: stationId } = station || {};

  const [submitReview, { called: reviewSubmitted, error: submitError, loading: isSubmittingReview }] = useMutation(SUBMIT_REVIEW);
  const { data, error, loading: isFetching, refetch } = useQuery(GET_REVIEW_SETTINGS, {
    skip: !isLoggedIn,
    variables: {
      stationId,
    },
  });
  const reviewSettings = data?.reviewSettings;
  const hasDisabledReviews = reviewSettings ? reviewSettings.disabled : false;
  const isDisabledMessage = reviewSettings ? reviewSettings.disabledMessage : '';

  // Build the translated rating categories
  const ratingCategories = useMemo(() => (reviewSettings?.ratingCategories || []).map(category => ({
    ...category,
    name: t(`rating${category.id}_name`, { ns: 'ratingCategories' }),
    question: t(`rating${category.id}_question`, { ns: 'ratingCategories' }),
  })), [reviewSettings, t]);

  const onOpenReviewModal = useCallback(() => {
    setShowMemberNameCustomization(false);
    setShowReviewModal(true);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.REVIEW_OPENED,
      attributes: { stationId, variant: 'review' },
    });
  }, [analytics, stationId]);

  const onShowModal = useCallback(() => {
    if (canSetMemberName && shouldShowMemberNameModal) {
      setShowMemberNameCustomization(true);
      setLastShownMemberNameModal(new Date());
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.RECEIVED_EDIT_USERNAME_PROMPT,
        attributes: { Context: 'Station Review' },
      });
    } else {
      onOpenReviewModal();
    }
  }, [analytics, canSetMemberName, onOpenReviewModal, setLastShownMemberNameModal, shouldShowMemberNameModal]);

  const onCloseReviewModal = useCallback(() => {
    setShowReviewModal(false);
    setCurrentQuestionIndex(0);
    setShowMemberNameCustomization(false);
    setRecordedRatings([]);
    setRecaptchaToken(null);
    setRecaptchaAttempted(false);

    if (reviewSubmitted) {
      refetch();
    }
  }, [refetch, reviewSubmitted]);

  const onReviewChange = useCallback(({ target }) => {
    setReviewText(target.value);
  }, []);

  const onReviewSubmit = useCallback(async (e) => {
    e.preventDefault();

    const responseToken = await executeRecaptcha('writeReview');
    setRecaptchaToken(responseToken);
    setRecaptchaAttempted(true);

    if (responseToken) {
      // exclude rating categories for which user selected NOT SURE option
      const ratedCategories = recordedRatings.filter(r => r.value !== RATING_EXPERIENCE.NOT_SURE);

      submitReview({
        variables: {
          stationId,
          body: reviewText,
          replyRequested: false,
          ratings: recordedRatings,
          recaptchaResponse: responseToken,
        },
      });

      analytics.tagEvent({
        name: ANALYTICS_EVENTS.REVIEW_SUBMITTED,
        attributes: {
          stationId,
          variant: 'review',
          rating_categories_rated: ratedCategories.length > 0,
        },
      });
    }
  }, [analytics, executeRecaptcha, recordedRatings, reviewText, stationId, submitReview]);

  const handleMemberNameChangeRequested = useCallback(() => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.CHANGE_USERNAME_OPENED,
      attributes: { Context: 'Review_Submission' },
    });
  }, [analytics]);

  const handleMemberNameUpdateFinish = useCallback(() => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.USERNAME_UPDATED,
      attributes: { Context: 'Review_Submission' },
    });
  }, [analytics]);

  const handleReviewCategoryRated = useCallback((categoryId, value) => {
    if (categoryId <= 0 || value < 0 || value > 3) {
      throw new Error(`Unexpected rating category or value: categoryId=${categoryId}, value=${value}`);
    }

    const nextQuestionIndex = currentQuestionIndex + 1;
    const updatedRatings = [].concat(recordedRatings, {
      categoryId,
      stationId,
      value,
    });
    setRecordedRatings(updatedRatings);
    setCurrentQuestionIndex(nextQuestionIndex);
  }, [stationId, recordedRatings, currentQuestionIndex]);

  if (!isLoggedIn) {
    return (
      <Button
        secondary
        as="a"
        href="/login"
        fluid
      >
        {notLoggedInLinkText || t('logInToRate', { ns: 'review' })}
      </Button>
    );
  }

  const shouldShowMessage = hasDisabledReviews || reviewSubmitted;
  const hideHeaderAndButtons = (!isFetching && !ratingCategories.length);

  const shouldShowSuccess = !isDisabledMessage?.length && typeof reviewSubmitted !== 'undefined' && !submitError?.length;
  const messageContent = shouldShowSuccess
    ? (
      <ThankYou
        messageType="review"
        onDone={onCloseReviewModal}
      />
    )
    : (
      <div className={cx('successContainer')}>
        <span className={cx('successSubHeader')}>
          {isDisabledMessage?.length
            ? t('disabledReviews', { ns: 'review' })
            : t('errorLoadingReviewQuestions', { ns: 'review' })
          }
        </span>
      </div>
    );

  let modalContent = <Loader />;
  if (ratingCategories.length) {
    if (currentQuestionIndex >= ratingCategories.length) {
      modalContent = (
        <Fragment>
          <WriteReview
            onReviewChange={onReviewChange}
            submitError={submitError}
          />
          {recaptchaAttempted && !recaptchaToken && (
            <Fragment>
              <br /><br />
              <Text as="p" color="orange">
                {t('verificationFailed', { ns: 'review' })}
              </Text>
            </Fragment>
          )}
        </Fragment>
      );
    } else if (ratingCategories[currentQuestionIndex]) {
      modalContent = (
        <ReviewQuestionItem
          item={ratingCategories[currentQuestionIndex]}
          onScoreChosen={handleReviewCategoryRated}
        />
      );
    }
  }

  return (
    <Fragment>
      {!error && (
        <Button
          primary
          fluid
          onClick={onShowModal}
        >
          {openModalLinkText || t('rateStation', { ns: 'review' })}
        </Button>
      )}
      {showMemberNameCustomization && (
        <EditMemberNameModal
          memberName={memberName}
          messageType="review"
          onCloseModal={onOpenReviewModal}
          onMemberNameChangeRequested={handleMemberNameChangeRequested}
          onMemberNameUpdated={handleMemberNameUpdateFinish}
          showModal
        />
      )}
      {showReviewModal && (
        <Modal
          forceIsShowing
          onClose={onCloseReviewModal}
          size="sm"
          bgClickable={false}
          content={({ close }) => (
            <div className={cx('modalBody')}>
              {shouldShowMessage
                ? messageContent
                : (
                  <Fragment>
                    {!hideHeaderAndButtons && (
                      <Fragment>
                        <Header as="h3">
                          {t('rateAndReview', { ns: 'review' })}
                        </Header>
                        <StationInfoBox station={station} />
                        <Divider />
                      </Fragment>
                    )}
                    {modalContent}
                    {!hideHeaderAndButtons && (
                      <Fragment>
                        <br />
                        <Divider />
                        <br />
                        {shouldShowMessage
                          ? (
                            <Button
                              primary
                              onClick={close}
                              className={cx('closeReviewModalButton')}
                            >
                              {t('actions.close', { ns: 'miscellaneous' })}
                            </Button>
                          )
                          : (
                            <Actions>
                              <Actions.Button
                                primary
                                fluid
                                loading={isSubmittingReview}
                                disabled={!executeRecaptcha || !reviewText}
                                onClick={onReviewSubmit}
                                tablet={8}
                                desktop={8}
                              >
                                {t('submitRatings', { ns: 'review' })}
                              </Actions.Button>
                              <Actions.Button
                                secondary
                                fluid
                                onClick={close}
                                tablet={4}
                                desktop={4}
                              >
                                {t('actions.cancel', { ns: 'miscellaneous' })}
                              </Actions.Button>
                            </Actions>
                          )
                        }
                      </Fragment>
                    )}
                  </Fragment>
                )}
            </div>
          )}
        />
      )}
    </Fragment>
  );
}

SubmitReview.propTypes = {
  notLoggedInLinkText: PropTypes.string,
  openModalLinkText: PropTypes.string,
  station: StationPropType.isRequired,
};

SubmitReview.defaultProps = {
  notLoggedInLinkText: undefined,
  openModalLinkText: undefined,
};
